import React from 'react';
import { Form, Button, Space, Typography, Row, Col, Alert, Divider, Input } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import { YouTubeLiveStream } from './types';

const { Text } = Typography;

interface ManualRTMPConfigProps {
  stream: YouTubeLiveStream;
  form: any;
  loading: boolean;
  onCancel: () => void;
  onConfigure: () => void;
  isLive?: boolean;
}

const ManualRTMPConfig: React.FC<ManualRTMPConfigProps> = ({
  stream,
  form,
  loading,
  onCancel,
  onConfigure,
  isLive = false
}) => {
  return (
    <div>
      {isLive && (
        <Alert
          message="RTMP settings are locked while playout is active"
          description="Stop the playout to change RTMP URL or stream key settings."
          type="warning"
          showIcon
          style={{ marginBottom: '16px' }}
        />
      )}
      <Alert
        message="Manual RTMP Configuration"
        description="Configure your stream by manually entering the RTMP URL and stream key. OAuth-dependent features like YouTube preview won't be available."
        type="info"
        icon={<SettingOutlined />}
        style={{ marginBottom: '24px' }}
      />

      <Form form={form} layout="vertical" initialValues={{
        rtmp_url: stream.rtmp_url || 'rtmp://a.rtmp.youtube.com/live2',
        stream_key: stream.stream_key || '',
        twitch_rtmp_url: stream.twitch_rtmp_url || 'rtmp://ingest.global-contribute.live-video.net/app',
        twitch_stream_key: stream.twitch_stream_key || '',
        caption_ingestion_url: stream.caption_ingestion_url || ''
      }}>
        <Form.Item
          name="rtmp_url"
          label="RTMP URL (Primary)"
          help={
            <div style={{ marginTop: '8px' }}>
              For YouTube: <Text code>rtmp://a.rtmp.youtube.com/live2</Text>
              <br />
              For local testing: <Text code copyable>rtmp://nginx-rtmp:1935/live/stream</Text>
              <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginTop: '4px' }}>
                (Make sure Docker containers are running for local testing)
              </Text>
            </div>
          }
          rules={[
            { required: true, message: 'Please enter RTMP URL' },
            {
              validator: (_, value) => {
                if (value && !value.startsWith('rtmp://') && !value.startsWith('rtmps://')) {
                  return Promise.reject(new Error('RTMP URL must start with rtmp:// or rtmps://'));
                }
                return Promise.resolve();
              }
            }
          ]}
        >
          <Input
            placeholder="rtmp://a.rtmp.youtube.com/live2 or rtmp://nginx-rtmp:1935/live/stream"
            prefix={<SettingOutlined />}
            disabled={isLive}
          />
        </Form.Item>

        <Form.Item
          name="stream_key"
          label="Stream Key (Primary)"
          rules={[
            { required: true, message: 'Please enter stream key' }
          ]}
        >
          <Input.Password
            placeholder="Enter your stream key"
            visibilityToggle
            disabled={isLive}
          />
        </Form.Item>

        <Divider orientation="left" style={{ marginTop: '24px', marginBottom: '16px' }}>
          <Text type="secondary" style={{ fontSize: '14px' }}>Secondary RTMP Target (Optional)</Text>
        </Divider>

        <Form.Item
          name="twitch_rtmp_url"
          label="RTMP URL (Secondary - Twitch)"
          help={
            <div style={{ marginTop: '8px' }}>
              For Twitch: <Text code>rtmp://ingest.global-contribute.live-video.net/app</Text>
              <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginTop: '4px' }}>
                Optional: Add a second RTMP target to stream to Twitch simultaneously
              </Text>
            </div>
          }
          rules={[
            {
              validator: (_, value) => {
                if (!value) {
                  return Promise.resolve();
                }
                if (!value.startsWith('rtmp://') && !value.startsWith('rtmps://')) {
                  return Promise.reject(new Error('RTMP URL must start with rtmp:// or rtmps://'));
                }
                // If twitch_rtmp_url is provided, check if twitch_stream_key is also provided
                const twitchStreamKey = form.getFieldValue('twitch_stream_key');
                if (value && !twitchStreamKey) {
                  return Promise.reject(new Error('Twitch - Stream Key is required when RTMP URL is provided'));
                }
                return Promise.resolve();
              }
            }
          ]}
        >
          <Input
            placeholder="rtmp://ingest.global-contribute.live-video.net/app"
            prefix={<SettingOutlined />}
            disabled={isLive}
          />
        </Form.Item>

        <Form.Item
          name="twitch_stream_key"
          label="Stream Key (Secondary - Twitch)"
          rules={[
            {
              validator: (_, value) => {
                if (!value) {
                  return Promise.resolve();
                }
                // If twitch_stream_key is provided, check if twitch_rtmp_url is also provided
                const twitchRtmpUrl = form.getFieldValue('twitch_rtmp_url');
                if (value && !twitchRtmpUrl) {
                  return Promise.reject(new Error('RTMP URL is required when stream key is provided'));
                }
                return Promise.resolve();
              }
            }
          ]}
        >
          <Input.Password
            placeholder="Enter your Twitch stream key"
            visibilityToggle
            disabled={isLive}
          />
        </Form.Item>

      </Form>

     

      <Row justify="space-between" align="middle">
        <Col>
          <Button onClick={onCancel}>
            Cancel
          </Button>
        </Col>
        <Col>
          <Button 
            type="primary" 
            loading={loading}
            onClick={onConfigure}
            icon={<SettingOutlined />}
          >
            Save configuration
          </Button>
        </Col>
      </Row>
    </div>
  );
};

export default ManualRTMPConfig;
